/**
 * `create_folders` — the batch folder tool, as a pure function (22.08.2026).
 *
 * Why a batch exists: every tool call on the Apiboo path is one POST carrying
 * all ~54 tool schemas (≈13k prompt tokens), and the loop re-enters per call.
 * With the old single-folder tool, "make a folder per resource" was N round
 * trips and N × 13k tokens. One call with N items is the structural fix —
 * nothing about prompt wording could have bought it.
 *
 * This is the D0 batch pattern every later batch tool copies:
 *   - input-shape errors (no collection, not an array, empty, > cap, an item
 *     that is not an object) abort BEFORE any write and return `{ error }`;
 *   - per-item failures CONTINUE — the item carries `error`, the rest land —
 *     and the result is `{ success: failed_count === 0, …counts, folders[] }`
 *     in input order with `index`;
 *   - exactly one store write and one save per call (the hook's job; this
 *     function only returns the next collection).
 *
 * Semantics: `name` may be a slash path ("Billing/Invoices"), optionally under
 * `path` (an ancestor path) and/or `parent_folder_id`. Segments are matched
 * case-sensitively against siblings in the CURRENT tree — including folders an
 * earlier item in the same call created — so "A" then "A/B" nests. A missing
 * ancestor is created; an existing leaf is reused with `existed: true` and its
 * id returned. Reuse destroys nothing, which makes a retried call safe.
 */
import {
  getFolders,
  getFolderById,
  createFolderInCollection,
  normalizeParentId,
} from '../../../utils/collectionFolders';

/** Items per call. Ids and names only, so the Apiboo path's 2,048-token output cap is not the bound. */
export const FOLDER_BATCH_MAX = 50;
/**
 * Folders (path segments) per call, across every item. Items alone bound
 * nothing: one item named "s0/s1/…/s4999" is 5,000 folders (measured 8.8 s,
 * synchronous, all persisted) — F2, Tester 22.08.2026. Counted BEFORE any
 * write, so an over-long call is a 422-style input error, not a partial tree.
 */
export const FOLDER_BATCH_MAX_SEGMENTS = 100;

/** "A / B//C" → ["A", "B", "", "C"] — empties kept so the caller can reject them. */
function splitPath(value) {
  if (value === undefined || value === null) return [];
  const text = String(value);
  if (!text.trim()) return [];
  return text.split('/').map((s) => s.trim());
}

/** Full slash path of a folder, root-first. */
function folderPath(collection, folderId) {
  const names = [];
  const seen = new Set();
  let current = normalizeParentId(folderId);
  while (current && !seen.has(current)) {
    seen.add(current);
    const folder = getFolderById(collection, current);
    if (!folder) break;
    names.unshift(folder.name);
    current = normalizeParentId(folder.parent_id);
  }
  return names.join('/');
}

function findChild(collection, parentId, name) {
  const pid = normalizeParentId(parentId);
  return getFolders(collection).find(
    (f) => normalizeParentId(f.parent_id) === pid && String(f.name) === name
  ) || null;
}

/** Create one folder and return { collection, folder } — the new folder is the one id that was not there before. */
function createChild(collection, parentId, name) {
  const before = new Set(getFolders(collection).map((f) => String(f.id)));
  const next = createFolderInCollection(collection, { name, parent_id: parentId });
  const folder = getFolders(next).find((f) => !before.has(String(f.id)));
  return { collection: next, folder };
}

/**
 * Apply a folder batch to a collection.
 *
 * @param {object} collection  the collection as it is in the store
 * @param {Array<{name: string, parent_folder_id?: string, path?: string}>} items
 * @returns {{ error: string } | { collection, folders, created_count, existing_count, failed_count }}
 */
export function applyFolderBatch(collection, items) {
  if (!collection || typeof collection !== 'object') return { error: 'Collection not found' };
  if (!Array.isArray(items)) return { error: 'folders must be an array of { name, parent_folder_id?, path? }' };
  if (items.length === 0) return { error: 'folders is empty — pass at least one { name }' };
  if (items.length > FOLDER_BATCH_MAX) {
    return { error: `Too many folders: ${items.length} (max ${FOLDER_BATCH_MAX} per call)` };
  }
  const badItem = items.findIndex((it) => !it || typeof it !== 'object' || Array.isArray(it));
  if (badItem !== -1) return { error: `folders[${badItem}] must be an object with a name` };
  const totalSegments = items.reduce(
    (n, it) => n + splitPath(it.path).length + splitPath(it.name).length,
    0
  );
  if (totalSegments > FOLDER_BATCH_MAX_SEGMENTS) {
    return {
      error: `Too many folders: ${totalSegments} path segments across all items (max ${FOLDER_BATCH_MAX_SEGMENTS} per call) — split the request`,
    };
  }

  let next = collection;
  const report = [];
  let created = 0;
  let existing = 0;
  let failed = 0;

  items.forEach((item, index) => {
    const rawName = item.name;
    const segments = [...splitPath(item.path), ...splitPath(rawName)];
    const label = typeof rawName === 'string' ? rawName.trim() : '';

    if (segments.length === 0) {
      report.push({ index, name: label, error: 'name is empty' });
      failed += 1;
      return;
    }
    if (segments.some((s) => s === '')) {
      report.push({ index, name: label, error: 'path has an empty segment' });
      failed += 1;
      return;
    }

    let parentId = normalizeParentId(item.parent_folder_id);
    if (parentId && !getFolderById(next, parentId)) {
      report.push({ index, name: label, error: `parent_folder_id ${parentId} not found` });
      failed += 1;
      return;
    }

    let leaf = null;
    let leafExisted = false;
    for (let i = 0; i < segments.length; i += 1) {
      const segment = segments[i];
      const isLeaf = i === segments.length - 1;
      const found = findChild(next, parentId, segment);
      if (found) {
        leaf = found;
        leafExisted = isLeaf;
      } else {
        const made = createChild(next, parentId, segment);
        next = made.collection;
        leaf = made.folder;
        if (!isLeaf) created += 1;
        leafExisted = false;
      }
      parentId = String(leaf.id);
    }

    if (leafExisted) existing += 1;
    else created += 1;

    report.push({
      index,
      name: leaf.name,
      path: folderPath(next, leaf.id),
      folder_id: String(leaf.id),
      parent_folder_id: normalizeParentId(leaf.parent_id),
      existed: leafExisted,
    });
  });

  return {
    collection: next,
    folders: report,
    created_count: created,
    existing_count: existing,
    failed_count: failed,
  };
}

/**
 * What crosses back to the model for a create_folders result: ids, names,
 * flags and per-item errors — never anything else the executor may add later.
 */
export function condenseFolderBatchResult(result) {
  if (!result || typeof result !== 'object') return result;
  return {
    success: result.success,
    collection_id: result.collection_id,
    created_count: result.created_count,
    existing_count: result.existing_count,
    failed_count: result.failed_count,
    folders: (result.folders || []).map((f) => (
      f.error
        ? { index: f.index, name: f.name, error: f.error }
        : {
          index: f.index,
          name: f.name,
          path: f.path,
          folder_id: f.folder_id,
          parent_folder_id: f.parent_folder_id,
          existed: f.existed,
        }
    )),
  };
}
